import { colors } from "app/popleads/components/ui-kit/colors";
import InputWithIcon from "app/popleads/components/ui-kit/Input";
import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import styled from "styled-components";

import moment from "moment";
import { StyledLabel } from "./ExtensionInputGroup";
import { useExtensionContext } from "../ExtensionContext";
import { FormattedNumber } from "react-intl";
import useGetMasterCampaigns from "app/popleads/hooks/useGetMasterCampaigns";
import useGetCampaignSubCampaigns from "app/popleads/hooks/useGetCampaignSubCampaigns";
import { toast } from "react-toastify";

const SearchContainer = styled.div`
    position: relative;
    width: 100%;
`;

const InputWrapper = styled.div`
    cursor: pointer;
    width: 100%;
`;

const ResultsList = styled.div`
    border: 1px solid #ccc;
    border-radius: 8px;
    background: #fff;
    padding: 15px;
    position: absolute;
    top: 65px;
    width: 100%;
    z-index: 999;
    display: ${(props) => (props.isOpen ? "block" : "none")};
    max-height: 300px;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #555;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
    }
`;

const ResultItem = styled.div`
    margin-bottom: 5px;
    padding: 4px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: #f1f1f1;
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

const ResultText = styled.div`
    display: flex;
    align-items: center;
    max-width: 100%;
    justify-content: space-between;
`;

const Name = styled.div`
    font-weight: 500;
    font-size: 14px;
    letter-spacing: -0.17px;
    margin-bottom: 8px;
    color: ${colors.l3rnTech[900]};
`;

const Info = styled.div`
    font-size: 12px;
    color: ${colors.neutral[600]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DateInfo = styled.div`
    font-size: 12px;
    color: ${colors.neutral[500]};
    margin-left: auto;
`;

export default function ExtensionCampaignAndSubCampaignInput({
    optionClick,
    isShowInput,
    placeholder,
    label = true,
    value,
    disabled = false,
    campaignType = "deal",
    masterId = null,
    mode = "master",
    preloadedSubCampaigns = [],
    excludeMainCampaign = null,
}) {
    const [searchText, setSearchText] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [campaignsLoaded, setCampaignsLoaded] = useState(false);
    const wrapperRef = useRef(null);
    const { setSelectedCampaignStartDate } = useExtensionContext();

    const { loading: masterLoading, masterCampaigns, getMasterCampaigns } = useGetMasterCampaigns();
    const { loading: subLoading } = useGetCampaignSubCampaigns();

    const loading = mode === "master" ? masterLoading : subLoading;
    const rawCampaigns = mode === "master" ? masterCampaigns : preloadedSubCampaigns;

    const campaigns = excludeMainCampaign
        ? rawCampaigns.filter((campaign) => campaign.ad !== excludeMainCampaign)
        : rawCampaigns;

    useEffect(() => {
        if (value) {
            if (typeof value === "object" && value.ad) {
                setSearchText(value.ad);
            } else if (typeof value === "string") {
                setSearchText(value);
            }
        }
    }, [value]);

    useEffect(() => {
        if (mode === "sub") {
            setCampaignsLoaded(false);
            setSearchText("");
            setIsOpen(false);
        }
    }, [masterId, mode]);

    useEffect(() => {
        if (mode === "sub" && preloadedSubCampaigns.length > 0) {
            setCampaignsLoaded(true);
        }
    }, [preloadedSubCampaigns, mode]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleInputFocus = async () => {
        if (!disabled && !loading) {
            if (mode === "master") {
                if (!campaignsLoaded) {
                    try {
                        await getMasterCampaigns(campaignType);
                        setCampaignsLoaded(true);
                        setIsOpen(true);
                    } catch (error) {
                        toast.error("Error fetching campaigns");
                    }
                } else {
                    setIsOpen(true);
                }
            } else if (mode === "sub") {
                if (preloadedSubCampaigns.length > 0) {
                    setIsOpen(true);
                } else {
                    toast.error("No sub campaigns available");
                }
            }
        }
    };

    const handleWrapperClick = async () => {
        if (!disabled) {
            await handleInputFocus();

            if (wrapperRef.current) {
                const input = wrapperRef.current.querySelector("input");
                if (input) {
                    input.focus();
                }
            }
        }
    };

    const formatDate = (date) => {
        if (!date) {
            return moment().format("DD MMM YYYY");
        }
        return moment(date).format("DD MMM YYYY");
    };

    const handleResultClick = (result) => {
        setSearchText(result.ad || "");
        setIsOpen(false);
        optionClick(result);
    };

    const clearSearch = () => {
        setSearchText("");
        setIsOpen(false);
        setCampaignsLoaded(false);
        optionClick(null);
    };

    return (
        <>
            {isShowInput && <SearchContainer />}
            <SearchContainer ref={wrapperRef}>
                {label && <StyledLabel>{label}</StyledLabel>}
                <InputWrapper onClick={handleWrapperClick}>
                    <InputWithIcon
                        variant="l3rnTech"
                        minLength={0}
                        placeholder={
                            placeholder ||
                            (mode === "master" ? "Click to select campaign" : "Click to select sub campaign")
                        }
                        type={"text"}
                        leftIcon={"search"}
                        value={searchText}
                        onFocus={handleInputFocus}
                        disabled={disabled}
                        rightIcon={
                            loading ? (
                                <Spinner animation="border" variant="l3rnTech" size="sm" />
                            ) : searchText.length > 0 ? (
                                "x"
                            ) : null
                        }
                        rightIconClick={clearSearch}
                        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
                    />
                </InputWrapper>

                <ResultsList isOpen={isOpen}>
                    {campaigns.map((result, index) => (
                        <ResultItem
                            key={index}
                            onClick={() => {
                                handleResultClick(result);
                                if (mode === "master") {
                                    setSelectedCampaignStartDate(result.basTarih);
                                }
                            }}
                        >
                            <Name>{result.ad}</Name>
                            <ResultText>
                                <Info>
                                    Exp. Revenue:{" "}
                                    {result.beklenenGelir ? (
                                        <FormattedNumber
                                            value={result.beklenenGelir}
                                            style="currency"
                                            currency="USD"
                                            maximumFractionDigits={0}
                                        />
                                    ) : (
                                        "-"
                                    )}
                                </Info>
                                <DateInfo>{formatDate(result.basTarih)}</DateInfo>
                            </ResultText>
                        </ResultItem>
                    ))}
                    {campaigns.length === 0 && !loading && isOpen && (
                        <div>{mode === "master" ? "No campaigns found." : "No sub campaigns found."}</div>
                    )}
                </ResultsList>
            </SearchContainer>
        </>
    );
}
